import {
  IKpiLookupItem,
  IKpiObjectiveContractSavePayload,
  IKpiObjectiveContractListFilters,
  IKpiObjectiveContractListResponse,
  IKpiObjectiveContractDetailResponse,
  IKpiObjectiveContractGeneratedPayload,
} from './objectivecontract.models';

interface IApiEnvelope<T> {
  ok: boolean;
  message?: string;
  data: T;
  errors?: Array<{ field?: string; code?: string; message: string }>;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      searchParams.append(key, `${value}`);
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(`/biz${url}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return (await response.json()) as T;
}

async function sendJson<T>(url: string, method: 'POST' | 'PUT' | 'DELETE', body?: any): Promise<T> {
  const response = await fetch(`/biz${url}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return (await response.json()) as T;
}

export async function fetchKpiLookup(module: string, search = '', limit = 50): Promise<IKpiLookupItem[]> {
  const query = buildQuery({ search, limit });
  const payload = await getJson<IApiEnvelope<{ items: IKpiLookupItem[] }>>(
    `/api/kpi/lookups/${module}${query}`
  );
  if (!payload.ok) throw new Error(payload.message || `Failed to load ${module}`);
  return payload.data.items || [];
}

export async function fetchObjectiveContracts(
  filters: IKpiObjectiveContractListFilters
): Promise<IKpiObjectiveContractListResponse> {
  const query = buildQuery({
    search: filters.search,
    staffix: filters.staffix,
    yearno: filters.yearno,
    stateix: filters.stateix,
    status: filters.status,
    page: filters.page,
    pagesize: filters.pagesize,
  });
  const payload = await getJson<IApiEnvelope<IKpiObjectiveContractListResponse>>(
    `/api/kpi/contracts${query}`
  );
  if (!payload.ok) throw new Error(payload.message || 'Failed to load KPI objective contracts');
  return payload.data;
}

export async function fetchObjectiveContract(contractix: string): Promise<IKpiObjectiveContractDetailResponse> {
  const payload = await getJson<IApiEnvelope<IKpiObjectiveContractDetailResponse>>(
    `/api/kpi/contracts/${contractix}`
  );
  if (!payload.ok) throw new Error(payload.message || 'Failed to load KPI objective contract');
  return payload.data;
}

export async function saveObjectiveContract(payloadBody: IKpiObjectiveContractSavePayload): Promise<any> {
  const contractix = payloadBody.contract.recix;
  const payload = await sendJson<IApiEnvelope<any>>(
    contractix ? `/api/kpi/contracts/${contractix}` : `/api/kpi/contracts`,
    contractix ? 'PUT' : 'POST',
    payloadBody
  );
  if (!payload.ok) throw new Error(payload.message || 'Failed to save KPI objective contract');
  return payload.data;
}

export async function deleteObjectiveContract(contractix: string): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(`/api/kpi/contracts/${contractix}`, 'DELETE');
  if (!payload.ok) throw new Error(payload.message || 'Failed to delete KPI objective contract');
  return payload.data;
}

export async function fetchObjectiveContractGeneratedPayload(
  contractix: string
): Promise<IKpiObjectiveContractGeneratedPayload> {
  const payload = await getJson<IApiEnvelope<IKpiObjectiveContractGeneratedPayload>>(
    `/api/kpi/contracts/${contractix}/document-payload`
  );
  if (!payload.ok) throw new Error(payload.message || 'Failed to generate KPI contract payload');
  return payload.data;
}
